import { useContext } from "react";
import { Select } from "antd5";

import { usePropertyOptions } from "lib/hooks/api/integrations/usePropertyOptions";
import FormItemWrapper, { errorStatus } from "../FormItemWrapper";
import { FormContext } from "../useFormDataContext";

import css from "../formStyles.module.scss";

type OptionsProps = {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  disabled: boolean;
  updateValue: (value: string) => void;
  errors: string;
  description?: string;
  freeTextFields: string[];
  /** The CRM object the property belongs to, when the form knows it. */
  entityName?: string;
};

export function Options({
  id,
  label,
  required,
  value,
  disabled,
  updateValue,
  errors,
  description,
  freeTextFields,
  entityName,
}: OptionsProps): JSX.Element {
  const formData = useContext(FormContext);

  // Filter out free text fields from formData to prevent unnecessary API calls since dynamic options shouldn't be matching against any property from them
  const filteredFormData = Object.fromEntries(
    Object.entries(formData).filter(([key]) => !freeTextFields.includes(key)),
  );

  const { data: values, isLoading } = usePropertyOptions(
    id.substring(id.lastIndexOf("/") + 1),
    filteredFormData,
    entityName,
  );

  return (
    <FormItemWrapper
      formIdentifier="string"
      label={label}
      required={required}
      errors={errors}
      description={description}
    >
      <Select
        status={errorStatus(errors)}
        id={id}
        loading={isLoading}
        allowClear={!required}
        defaultValue={value}
        labelInValue
        placeholder="Select..."
        className={css.fullWidth}
        onChange={updateValue}
        disabled={disabled}
        showSearch
        options={values?.map((x) => ({
          label: x.label,
          value: x.id,
        }))}
        filterOption={(inputValue, option) => {
          if (!option) return false;

          return option.label.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase());
        }}
        onSelect={(_, option) => {
          updateValue(option.value);
        }}
        getPopupContainer={(node) => node}
      />
    </FormItemWrapper>
  );
}
